'use client'

import { yupResolver } from '@hookform/resolvers/yup'
import {
    Button,
    ButtonTypes,
    ButtonVariants,
    FormField,
    FormInput,
    InlineNotification,
    NotificationVariant,
    Text,
    TextTypes,
} from '../../../MyStichtingOpenComponents/src/static'
import Link from 'next/link'
import { ParsedUrlQuery } from 'querystring'
import { useEffect, useState, type ReactElement } from 'react'
import { getLoginQueryParams } from '@/helpers/getLoginQueryParams'
import { Controller, useForm } from 'react-hook-form'
import * as yup from 'yup'
import { Routes } from '@/constants/routeConstants'
import { useTranslations } from '@/helpers/useTranslations'
import { UseLoginHook } from './hooks/UseLoginHook'

export const LoginForm = (): ReactElement => {
    const [showPassword, setShowPassword] = useState<boolean>(false)
    const [queryStringParams, setQueryStringParams] = useState<ParsedUrlQuery>({})
    const translate = useTranslations()
    const { isLoading, loginHandler, loginError } = UseLoginHook()

    const ERROR_MSG: { [key: string]: string } = {
        REQUIRED: translate('ERROR_REQUIRED'),
        E_MAIL: translate('ERROR_E_MAIL'),
    }

    const validationSchema = yup.object().shape({
        email: yup.string().required(ERROR_MSG.REQUIRED),
        password: yup.string().required(ERROR_MSG.REQUIRED),
    })

    const {
        control,
        handleSubmit,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(validationSchema),
        defaultValues: {
            email: '',
            password: '',
        },
    })

    useEffect(() => {
        setQueryStringParams(
            getLoginQueryParams(`${window.location.pathname}${window.location.search}`),
        )
    }, [])

    const onSubmit = async (data: { email: string; password: string }) => {
        const newDataObj = {
            ...data,
            ...queryStringParams,
        }
        await loginHandler(newDataObj)
    }

    const showPasswordHandler = (): void => setShowPassword((current: boolean) => !current)

    return (
        <form>
            <div className="p-8 sm:p-12 md:p-24 max-w-xl">
                <Text type={TextTypes.HEADING_1} className="mb-8">
                    {translate('TITLE')}
                </Text>

                <FormField
                    name="email"
                    label={translate('MAIL_ADDRESS_LABEL')}
                    className="mb-6"
                    error={errors?.email?.message || undefined}
                >
                    <Controller
                        control={control}
                        name="email"
                        render={({ field: { onChange, value } }) => (
                            <FormInput
                                value={value}
                                name="email"
                                onChange={(value: string) => onChange({ target: { value } })}
                                error={!!errors?.email?.message}
                            />
                        )}
                    />
                </FormField>

                <FormField
                    name="password"
                    label={translate('PASSWORD_LABEL')}
                    className="mb-6"
                    error={errors?.password?.message || undefined}
                >
                    <Controller
                        control={control}
                        name="password"
                        render={({ field: { onChange, value } }) => (
                            <FormInput
                                value={value}
                                name="password"
                                type={showPassword ? 'text' : 'password'}
                                suffix={showPassword ? translate('PASSWORD_HIDE') : translate('PASSWORD_SHOW')}
                                onChange={(value: string) => onChange({ target: { value: value } })}
                                onClickSuffix={showPasswordHandler}
                                error={!!errors?.password?.message}
                            />
                        )}
                    />
                </FormField>

                {loginError && (
                    <InlineNotification
                        className="mt-6"
                        id="go"
                        message={translate('PASSWORD_DOES_NOT_MATCH')}
                        variant={NotificationVariant.ERROR}
                        dismissable={false}
                    />
                )}

                <Button
                    loading={isLoading}
                    type={ButtonTypes.SUBMIT}
                    variant={ButtonVariants.PRIMARY}
                    className="mb-6 mt-6"
                    onClick={handleSubmit(onSubmit)}
                    disabled={isLoading}
                >
                    {translate('LOGIN_BUTTON')}
                </Button>

                <Text className="my-2">
                    {translate('FOOTER_NO_ACCOUNT')}{' '}
                    <Link target="_blank" href={Routes.REGISTRATION_APP}>
                        {translate('FOOTER_REGISTRATION')}
                    </Link>
                </Text>

                <Text type={TextTypes.LINK}>
                    <Link href={Routes.RESET_PASSWORD}>{translate('FOOTER_FORGOT_PASSWORD')}</Link>
                </Text>
            </div>
        </form>
    )
}
